import type { FocusTrap } from 'focus-trap';
import { Component } from '@grafikr/components';
import * as focusTrap from 'focus-trap';

import { getGender } from 'utils/gender';
import { NonNullish } from 'utils/nullish-check';
import repaint from 'utils/repaint';
import { dispatch } from 'utils/theme';

type ScrollDirection = 'up' | 'down';

type MenuOpenOptions = {
  focusTrap?: boolean;
};

export default Component((node) => {
  const MODAL_OPEN_CLASS = 'header--modal-open';
  const MODAL_SCROLLED_CLASS = 'header--modal-scrolled';
  const SEARCH_OPEN_CLASS = 'header--search-open';
  const STICKY_CLASS = 'header--sticky';
  const VISIBLE_CLASS = 'header--visible';
  const NAV_ITEM_ACTIVE_CLASS = 'header__nav-item--active';
  const MEGAMENU_ACTIVE_CLASS = 'header__megamenu--active';
  const MOBILE_MENU_TOGGLE_OPEN_CLASS = 'header__mobile-menu-toggle--open';
  const ANNOUNCEMENT_BAR_HEIGHT_PROPERTY = '--announcement-bar-height';
  const HEADER_HEIGHT_PROPERTY = '--header-height';
  const SCROLL_THRESHOLD = 4;
  const MEGAMENU_DELAY = 250;

  const megamenuTriggers = Array.from(
    node.querySelectorAll<HTMLElement>('[data-megamenu-trigger]'),
  );
  const mobileMenuToggle = NonNullish(node.querySelector<HTMLElement>('[data-mobile-menu-toggle]'));

  let previousY = window.scrollY;

  const handleScroll = () => {
    const currentY = window.scrollY;
    const scrollDirection: ScrollDirection = currentY < previousY ? 'up' : 'down';

    const computedStyle = getComputedStyle(document.documentElement);
    const announcementBarHeight = parseInt(
      computedStyle.getPropertyValue(ANNOUNCEMENT_BAR_HEIGHT_PROPERTY),
    );
    const headerHeight = parseInt(computedStyle.getPropertyValue(HEADER_HEIGHT_PROPERTY));

    const isBeyondAnnouncementBar =
      currentY > announcementBarHeight || (announcementBarHeight === 0 && currentY > 0);
    const isBeyondHeader = currentY > announcementBarHeight + headerHeight;

    switch (scrollDirection) {
      case 'down':
        if (isBeyondHeader && !node.classList.contains(STICKY_CLASS)) {
          node.style.transition = 'none';
          node.classList.add(STICKY_CLASS);

          setTimeout(() => {
            node.style.transition = '';
          }, 0);
        }

        if (currentY - previousY > SCROLL_THRESHOLD && !node.classList.contains(MODAL_OPEN_CLASS)) {
          node.classList.remove(VISIBLE_CLASS);
          document.documentElement.style.setProperty('--header-visible-height', `0px`);
        }

        break;

      case 'up':
        if (!isBeyondAnnouncementBar) {
          node.classList.remove(STICKY_CLASS);
          node.classList.remove(VISIBLE_CLASS);
        }

        if (isBeyondHeader && previousY - currentY > SCROLL_THRESHOLD) {
          node.classList.add(VISIBLE_CLASS);

          const visibleHeight = node.getBoundingClientRect().height;
          document.documentElement.style.setProperty(
            '--header-visible-height',
            `${visibleHeight}px`,
          );
        }

        break;
    }

    previousY = currentY;
  };

  const handleHeaderModals = () => {
    document.addEventListener('header-modal:open', () => {
      node.classList.add(MODAL_OPEN_CLASS);
    });

    document.addEventListener('header-modal:close', () => {
      node.classList.remove(MODAL_OPEN_CLASS);
    });

    document.addEventListener('header-modal:scroll', ({ detail }) => {
      node.classList.toggle(MODAL_SCROLLED_CLASS, detail.scrolled);
    });
  };

  const handleSearchModal = () => {
    document.addEventListener('search-modal:open', () => {
      node.classList.add(SEARCH_OPEN_CLASS);
    });

    document.addEventListener('search-modal:close', () => {
      node.classList.remove(SEARCH_OPEN_CLASS);
    });
  };

  const initMegamenus = () => {
    const megamenus = Array.from(node.querySelectorAll<HTMLElement>('[data-megamenu]'));
    const triggers = new Map(
      megamenus.map((megamenu) => {
        return [
          megamenu,
          NonNullish(
            node.querySelector<HTMLElement>(`[data-megamenu-id="${megamenu.dataset.megamenuId}"]`),
          ),
        ];
      }),
    );
    const traps = new Map<HTMLElement, FocusTrap>();

    const isActive = (megamenu: HTMLElement) => megamenu.classList.contains(MEGAMENU_ACTIVE_CLASS);

    const openMegamenu = (megamenu: HTMLElement, options?: MenuOpenOptions) => {
      // Close all active
      megamenus
        .filter((item) => item !== megamenu)
        .filter(isActive)
        .forEach(closeMegamenu);

      const trigger = NonNullish(triggers.get(megamenu));

      node.classList.add(MODAL_OPEN_CLASS);

      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      document.documentElement.style.overflow = 'hidden';

      trigger.classList.add(NAV_ITEM_ACTIVE_CLASS);
      megamenu.hidden = false;
      repaint(megamenu);
      megamenu.classList.add(MEGAMENU_ACTIVE_CLASS);
      trigger.ariaExpanded = 'true';

      if (options?.focusTrap) {
        let trap = traps.get(megamenu);
        if (trap === undefined) {
          trap = focusTrap.createFocusTrap(megamenu, {
            onDeactivate: () => {
              closeMegamenu(megamenu);
            },
          });
          traps.set(megamenu, trap);
        }

        trap.activate();
      }

      dispatch('navigation-open', {
        label: trigger.textContent?.replace(/\n/g, '').trim() ?? 'Desktop',
        gender: getGender(),
      });
    };

    const closeMegamenu = (megamenu: HTMLElement) => {
      const trigger = NonNullish(triggers.get(megamenu));
      const hoveredTrigger = megamenuTriggers.find((tr) => tr.matches(':hover'));
      const hoveredMegamenu = megamenus.find((m) => m.matches(':hover'));

      if (hoveredTrigger === undefined && hoveredMegamenu === undefined) {
        node.classList.remove(MODAL_OPEN_CLASS);
      }

      megamenu.classList.remove(MEGAMENU_ACTIVE_CLASS);
      trigger.classList.remove(NAV_ITEM_ACTIVE_CLASS);

      megamenu.addEventListener(
        'transitionend',
        () => {
          if (megamenu.classList.contains(MEGAMENU_ACTIVE_CLASS)) {
            return;
          }

          megamenu.hidden = true;
          trigger.ariaExpanded = 'false';
        },
        { once: true },
      );

      document.documentElement.style.overflow = '';
      document.body.style.paddingRight = '';

      document.dispatchEvent(new CustomEvent('header-modal:close'));
    };

    const toggleMegamenu = (megamenu: HTMLElement, options?: MenuOpenOptions) => {
      if (isActive(megamenu)) {
        closeMegamenu(megamenu);
      } else {
        openMegamenu(megamenu, options);
      }
    };

    megamenus.forEach((megamenu) => {
      const trigger = NonNullish(triggers.get(megamenu));
      const link = NonNullish(trigger?.querySelector('a'));

      const onLeave = (event: MouseEvent) => {
        if (!isActive(megamenu)) {
          return;
        }

        setTimeout(() => {
          if (
            trigger.contains(event.relatedTarget as Node) ||
            megamenu.contains(event.relatedTarget as Node)
          ) {
            return;
          }

          if (trigger.matches(':hover') || megamenu.matches(':hover')) {
            return;
          }

          closeMegamenu(megamenu);
        }, MEGAMENU_DELAY);
      };

      trigger.addEventListener('mouseleave', onLeave);
      megamenu.addEventListener('mouseleave', onLeave);

      trigger.addEventListener('mouseover', () => {
        setTimeout(() => {
          if (!trigger.matches(':hover')) {
            return;
          }

          openMegamenu(megamenu);
        }, MEGAMENU_DELAY);
      });

      link.addEventListener('keydown', (event) => {
        // Check is event key is "SPACE"
        if (event.key !== ' ') {
          return;
        }

        event.preventDefault();

        toggleMegamenu(megamenu, { focusTrap: true });
      });
    });
  };

  const initMobileMenuToggle = () => {
    document.addEventListener('mobile-menu:open', () => {
      mobileMenuToggle.classList.add(MOBILE_MENU_TOGGLE_OPEN_CLASS);
    });

    document.addEventListener('mobile-menu:close', () => {
      mobileMenuToggle.classList.remove(MOBILE_MENU_TOGGLE_OPEN_CLASS);
    });
  };

  window.addEventListener('scroll', handleScroll);

  handleHeaderModals();
  handleSearchModal();
  initMegamenus();
  initMobileMenuToggle();
});
